import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { CoachPrivateProfileProvider } from "~/contexts/coach";
import { getCookie, setCookie } from "@tanstack/react-start/server";
import { trace } from "@opentelemetry/api";
import type { CoachSelect } from "~/lib/schemas";
import z from "zod";
import { createServerFn } from "@tanstack/react-start";

/**
 * Loads coach by locationId from URL or cookie, sets cookie if valid
 * Throws redirect if no valid coach found
 */
async function loadCoachFromUrlOrCookie(
  locationId: string | null | undefined,
): Promise<CoachSelect> {
  let resolvedLocationId = locationId ?? null;

  // If no locationId provided, try to get from cookie
  if (!resolvedLocationId) {
    const cookie = getCookie("locationId");
    if (cookie) {
      resolvedLocationId = cookie;
    }
  }

  if (!resolvedLocationId || "string" !== typeof resolvedLocationId) {
    throw redirect({
      to: "/coach/unauthenticated",
    });
  }

  try {
    const { getCoachByLocationId } = await import("~/lib/server/coach");
    const coach = (await getCoachByLocationId(resolvedLocationId)) ?? null;

    if (!coach) {
      throw redirect({
        to: "/coach/unauthenticated",
      });
    }

    // Set cookie for future requests
    setCookie("locationId", resolvedLocationId, {
      path: "/",
      secure: true,
      sameSite: "none",
      expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30), // 30 days
    });

    trace.getActiveSpan()?.setAttribute("coachId", coach.id);

    return coach;
  } catch (error) {
    console.error(error);

    // For other errors, redirect to unauthenticated
    throw redirect({
      to: "/coach/unauthenticated",
    });
  }
}

/**
 * Loads coach with all relations for the backend route
 */
const loadCoachWithRelations = createServerFn({ method: "GET" })
  .validator(z.object({ locationId: z.string().nullable().optional() }))
  .handler(async ({ data }) => {
    const { locationId } = data;
    const coach = await loadCoachFromUrlOrCookie(locationId);

    // Fetch the coach with all relations
    const { getDBWithInit } = await import("~/lib/db");
    const coachWithRelations = await (
      await getDBWithInit()
    ).query.coaches.findFirst({
      where: (coaches, { eq }) => eq(coaches.id, coach.id),
      with: {
        profile: true,
        details: true,
        mainOffer: {
          with: {
            tags: true,
            benefits: true,
          },
        },
        resources: true,
        certifications: true,
        testimonials: true,
        results: true,
      },
    });

    return coachWithRelations;
  });

export const Route = createFileRoute("/coach/_backend")({
  validateSearch: (search) =>
    z
      .object({
        locationId: z.string().optional(),
      })
      .optional()
      .parse(search),
  loaderDeps: ({ search }) => ({
    locationId: search?.locationId ?? null,
  }),
  beforeLoad: async ({ search }) => {
    const coach = await loadCoachWithRelations({
      data: { locationId: search?.locationId },
    });

    // Ensure coach data is present - if not, this is a configuration error
    if (!coach) {
      throw new Error("Coach data not found - middleware configuration error");
    }
    return { coach };
  },
  loader: async ({ context }) => {
    return {
      coach: context.coach,
    };
  },
  component: CoachLayout,
});

function CoachLayout() {
  const { coach } = Route.useLoaderData();

  return (
    <CoachPrivateProfileProvider coachProfile={coach}>
      <Outlet />
    </CoachPrivateProfileProvider>
  );
}
